import { type RefObject, useEffect } from "react";

const nonTextInputs = new Set(["checkbox", "radio", "button", "submit", "reset", "file", "color", "range", "image", "hidden"]);

/** Whether focusing the element brings up the on-screen keyboard; a select opens a picker instead. */
function bringsUpKeyboard(element: Element | null): boolean {
  if (!(element instanceof HTMLElement)) return false;
  if (element instanceof HTMLTextAreaElement) return !element.readOnly && !element.disabled;
  if (element instanceof HTMLInputElement) return !nonTextInputs.has(element.type) && !element.readOnly && !element.disabled;
  return element.isContentEditable;
}

/**
 * The keyboard counts as open when a text field has the focus and the viewport has shrunk clearly below the largest height seen at the
 * same width. Keying by width keeps a turn of the device apart from the keyboard; the margin absorbs a browser bar that shows and hides.
 */
function keyboardOpen(height: number, largestHeight: number, textFocused: boolean): boolean {
  return textFocused && largestHeight - height > Math.max(100, largestHeight * 0.15);
}

/**
 * Marks the element with data-keyboard="open" while the on-screen keyboard of a touch device is up. With
 * interactive-widget=resizes-content the keyboard shrinks the viewport, and fixed bars at the bottom would sit above it.
 */
export function useOnScreenKeyboard(target: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const element = target.current;
    if (!element || typeof window.matchMedia !== "function") return;
    const coarse = window.matchMedia("(pointer: coarse)");
    const largestByWidth = new Map<number, number>();
    const update = () => {
      const viewport = window.visualViewport;
      // Multiplying by the scale keeps pinch zoom from looking like a smaller viewport.
      const height = viewport ? viewport.height * viewport.scale : window.innerHeight;
      const width = document.documentElement.clientWidth;
      const largest = Math.max(largestByWidth.get(width) ?? 0, height);
      largestByWidth.set(width, largest);
      const open = coarse.matches && keyboardOpen(height, largest, bringsUpKeyboard(document.activeElement));
      if (open) element.dataset.keyboard = "open";
      else delete element.dataset.keyboard;
    };
    update();
    const viewport = window.visualViewport;
    viewport?.addEventListener("resize", update);
    window.addEventListener("resize", update);
    document.addEventListener("focusin", update);
    document.addEventListener("focusout", update);
    coarse.addEventListener("change", update);
    return () => {
      viewport?.removeEventListener("resize", update);
      window.removeEventListener("resize", update);
      document.removeEventListener("focusin", update);
      document.removeEventListener("focusout", update);
      coarse.removeEventListener("change", update);
      delete element.dataset.keyboard;
    };
  }, [target]);
}
